import PropTypes from 'prop-types';
import React from 'react';

import { getBgColor } from '$utilities/data';
import { useLocale } from '$contexts/locale.jsx';
import Image from '$partials/media/image';

/* =========================================================================  */
/* Component(s) */
/* =========================================================================  */

/**
 * @typedef {PropTypes.InferProps<typeof propTypes>} Props
 */
const ArticleFeaturedListingItem = ({
    article,
    backgroundColor
}) => {
    const { formatURI } = useLocale();

    const bg = getBgColor(backgroundColor);

    if (!article) { return null; }

    return (
        <a
            className='group'
            href={formatURI(`/articles/${article.slug}`)}>
            <div
                className={`${backgroundColor ? 'px-20 md:px-50 -mx-10 sm:mx-0 lg:-mx-30' : 'px-10 sm:px-20 pb-40'} pt-30`}
                style={{ backgroundColor: bg }}>
                <div className='md:pr-100'>
                    <h2 className='type-style-2 inline mr-10'>{article.title}</h2>
                </div>

                <div className='mt-30 flex items-center'>
                    {article.activities?.items.length > 0 && (
                        <>
                            <div className='mr-5'>
                                <img
                                    alt={article.activities?.items?.[0].icon.alt}
                                    className='pointer-events-none'
                                    height='23'
                                    loading='lazy'
                                    role='presentation'
                                    src={article.activities?.items?.[0].icon.src}
                                    width='23' />
                            </div>

                            <p className='inline-block type-style-7 text-orange '>
                                {article.activities.items[0]?.title}
                            </p>
                        </>
                    )}
                    {article.author && (
                        <p className='inline-block type-style-7 text-dark-grey w-3/12 ml-40 '>
                            By {article.author.name}
                        </p>
                    )}
                </div>

                {article?.listingFields?.listingImage && !article?.displayHeroImageWhole && (
                    <figure className='mt-30'>
                        <div
                            className='min-h-[200px] sm:min-h-[422px] md:min-h-[475px] w-full bg-contain bg-repeat-x'
                            style={{backgroundImage: `url(${article?.listingFields?.listingImage?.src})`}}
                            title={article?.listingFields?.listingImage?.title}/>
                    </figure>
                )}

                {article?.listingFields?.listingImage && article?.displayHeroImageWhole && (
                    <figure className='mt-30 aspect-[5/2]'>
                        <Image
                            {...article?.listingFields?.listingImage}
                            className='w-full h-full object-cover'
                            containerClassName='w-full h-full relative'
                            height='100%'
                            loadWidth={1920}
                            width='100%' />
                    </figure>
                )}
            </div>
        </a>
    );
};

/* =========================================================================  */
/* Typing and export(s) */
/* =========================================================================  */

const propTypes = {
    article: PropTypes.shape({
        activities: PropTypes.object,
        author: PropTypes.shape({
            name: PropTypes.string,
        }),
        displayHeroImageWhole: PropTypes.bool,
        publicationDate: PropTypes.string,
        listingFields: PropTypes.shape({
            listingImage: PropTypes.object
        }),
        slug: PropTypes.string,
        title: PropTypes.string,
        topics: PropTypes.shape({
            items: PropTypes.array,
        }),
    }),
    backgroundColor: PropTypes.string,
    image: PropTypes.object
};

ArticleFeaturedListingItem.propTypes = propTypes;

export default ArticleFeaturedListingItem;
